import React from 'react';
import { AiOutlineMail } from 'react-icons/ai';
import { FaArrowRight, FaWindows } from 'react-icons/fa6';
import { SiOpenai, SiClaude } from 'react-icons/si';
import { TiDocumentText } from 'react-icons/ti';
import SeoHead from '../components/SeoHead';

const exampleArrow = {
  position: 'absolute',
  right: '1.25rem',
  bottom: '1.25rem',
  color: '#C03626',
  fontSize: '1.1rem',
};

const Losninger = () => (
  <div style={pageStyle}>
    <SeoHead
      title="Agenter | AICE"
      description="AICE leverer AI-løsninger til videnstunge virksomheder. Vi udvikler både kendte og nye AI-agenter og implementerer dem i jeres ChatGPT- eller Copilot-miljø."
      canonicalPath="/losninger"
    />

    <section style={heroSection}>
      <p style={eyebrow}>Agenter</p>
      <h1 style={heroTitle}>AI-løsninger der passer til jeres måde at arbejde på</h1>
      <p style={heroText}>
        AICE bygger AI-agenter og digitale assistenter til videnstunge teams. Vi tilbyder konkrete Løsninger, men udvikler også nye agenter. Vores vigtigste fokus er at bygge værktøjer, der reelt understøtter og forbedrer arbejdsprocesserne i hverdagen, ikke blot teknologi for teknologiens skyld. 
      </p>
    </section>

    <section style={section}>
      <div style={sectionHeader}>
        <h2 style={sectionTitle}>Se eksemplerne vi allerede har bygget</h2>
        <p style={sectionText}>
          Løsningerne viser, hvordan AICE kombinerer AI-agenter, governance
          og brugeroplevelse, så værktøjerne bliver en naturlig del af arbejdsdagen.
        </p>
      </div>
      <div style={exampleGrid}>
        {solutionExamples.map((item) => (
          <a
            key={item.title}
            href={item.href}
            style={exampleCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 22px 44px rgba(192, 54, 38, 0.18)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = exampleCard.boxShadow;
            }}
          >
            <div style={exampleBadge}>
              <item.Icon size={26} />
            </div>
            <h3 style={exampleTitle}>{item.title}</h3>
            <p style={exampleText}>{item.text}</p>
            <span style={exampleArrow} aria-hidden="true">
              <FaArrowRight />
            </span>
          </a>
        ))}
      </div>
    </section>

    <section style={section}>
      <div style={sectionHeader}>
        <h2 style={sectionTitle}>Vi bygger også det, I mangler</h2>
        <p style={sectionText}>
          Har I en opgave, der kræver et særligt workflow eller et specialiseret datasæt? Vi designer og udvikler nye
          AI-agenter, som passer til jeres processer, jeres data og jeres sikkerhedskrav. Det kan være alt fra
          sagsforberedelse og research til dokumentopsummering, kvalitetstjek eller mødeforberedelse.
        </p>
      </div>
      <div style={featureGrid}>
        {customFeatures.map((item) => (
          <div key={item.title} style={featureRow}>
            <div style={featureIconText}>{item.icon}</div>
            <div>
              <h3 style={featureTitle}>{item.title}</h3>
              <p style={featureText}>{item.text}</p>
            </div>
          </div>
        ))}
      </div>
    </section>

    <section style={highlightSection}>
      <div style={highlightCard}>
        <div style={envGrid}>
          <div style={envRow}>
            <div style={envIcon}>
              <FaWindows />
            </div>
            <p style={envText}>Implementeres i jeres Copilot-miljø</p>
          </div>
          <div style={envRow}>
            <div style={envIcon}>
              <SiOpenai />
            </div>
            <p style={envText}>Implementeres i jeres ChatGPT-miljø</p>
          </div>
          <div style={envRow}>
            <div style={envIcon}>
              <SiClaude />
            </div>
            <p style={envText}>Implementeres i jeres Claude-miljø</p>
          </div>
        </div>
      </div>
    </section>

    <section style={section}>
      <div style={sectionHeader}>
        <h2 style={sectionTitle}>Sådan arbejder vi</h2>
        <p style={sectionText}>
          Vi starter med jeres forretning og udvælger de use cases, der skaber størst værdi. Derefter designer vi
          agentens workflow, bygger prototyper og sikrer forankring gennem træning og governance.
        </p>
      </div>
      <div style={stepsGrid}>
        {steps.map((step) => (
          <div key={step.title} style={stepCard}>
            <div style={stepNumber}>{step.number}</div>
            <h3 style={featureTitle}>{step.title}</h3>
            <p style={featureText}>{step.text}</p>
          </div>
        ))}
      </div>
    </section>
  </div>
);

const customFeatures = [
  {
    title: 'Nye AI-agenter på jeres data',
    text: 'Vi bygger specialiserede agenter, der kan arbejde med interne dokumenter, systemer og kundedata.',
    icon: '✦',
  },
  {
    title: 'Workflow der matcher jeres praksis',
    text: 'Agenten designes til jeres processer, godkendelser og kvalitetskrav – ikke omvendt.',
    icon: '↔',
  },
  {
    title: 'Sikkerhed og governance',
    text: 'Vi sikrer sporbarhed, datakontrol og ansvarlig brug – uanset platform og compliancekrav.',
    icon: '✔',
  },
];

const solutionExamples = [
  {
    title: 'Emailagenten',
    badge: 'Kundeservice & salg',
    text: 'Udkast til alle indgående mails med fælles tone of voice, kvalitetssikring og menneskelig godkendelse.',
    href: '/losninger/emailagenten',
    Icon: AiOutlineMail,
  },
  {
    title: 'Udbudsagenten',
    badge: 'Salg & tilbud',
    text: 'Sætter struktur på udbud, samler dokumenter og genererer udkast, så teams svarer hurtigere og mere præcist.',
    href: '/losninger/udbudsagent',
    Icon: TiDocumentText,
  },
];

const steps = [
  {
    number: '01',
    title: 'Discovery',
    text: 'Vi kortlægger behov, data og processer og prioriterer de use cases, der giver hurtig effekt.',
  },
  {
    number: '02',
    title: 'Design & prototype',
    text: 'Vi designer agentens workflow og bygger en prototype, der kan testes i jeres hverdag.',
  },
  {
    number: '03',
    title: 'Implementering',
    text: 'Vi integrerer i jeres miljø og sikrer kvalitet, sikkerhed og governance fra start.',
  },
  {
    number: '04',
    title: 'Forankring',
    text: 'Vi træner teams, måler effekt og optimerer løbende, så værdien bliver ved.',
  },
];

const pageStyle = {
  background: '#FFF9F8',
  minHeight: '100vh',
  color: '#1f1f1f',
  padding: '4rem clamp(1.5rem, 5vw, 4rem)',
};

const heroSection = {
  maxWidth: 1000,
  margin: '0 auto 3.5rem',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
};

const eyebrow = {
  textTransform: 'uppercase',
  letterSpacing: '0.22em',
  color: '#C03626',
  fontWeight: 700,
  fontSize: '0.85rem',
};

const heroTitle = {
  fontSize: '2.8rem',
  margin: 0,
  color: '#2A0D05',
};

const heroText = {
  fontSize: '1.15rem',
  lineHeight: 1.7,
  color: '#4A2E25',
  margin: 0,
};

const ctaRow = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: '1rem',
  marginTop: '0.5rem',
};

const primaryCta = {
  background: 'linear-gradient(135deg, #C03626, #FF6F61)',
  color: '#fff',
  padding: '0.8rem 1.6rem',
  borderRadius: '999px',
  textDecoration: 'none',
  fontWeight: 700,
};

const secondaryCta = {
  background: '#fff',
  color: '#C03626',
  padding: '0.8rem 1.6rem',
  borderRadius: '999px',
  textDecoration: 'none',
  fontWeight: 700,
  border: '1px solid #C03626',
};

const section = {
  maxWidth: 1100,
  margin: '0 auto 3.5rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '2rem',
};

const sectionHeader = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.8rem',
};

const sectionTitle = {
  fontSize: '2rem',
  margin: 0,
  color: '#2A0D05',
};

const sectionText = {
  fontSize: '1.05rem',
  lineHeight: 1.7,
  color: '#4A2E25',
  margin: 0,
};

const featureGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
  gap: '1.5rem',
};

const featureRow = {
  display: 'flex',
  gap: '0.8rem',
  alignItems: 'flex-start',
};

const featureIconText = {
  color: '#C03626',
  fontWeight: 700,
  fontSize: '1.2rem',
  lineHeight: 1.2,
};

const featureTitle = {
  margin: 0,
  fontSize: '1.25rem',
  color: '#2B1610',
};

const featureText = {
  margin: 0,
  color: '#5C3A32',
  lineHeight: 1.6,
};

const exampleGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: '1.5rem',
};

const exampleCard = {
  background: '#fff',
  borderRadius: '18px',
  padding: '1.75rem',
  border: '1px solid #F3D9D2',
  boxShadow: '0 16px 32px rgba(192, 54, 38, 0.08)',
  textDecoration: 'none',
  color: 'inherit',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  position: 'relative',
  paddingBottom: '2.5rem',
};

const exampleBadge = {
  alignSelf: 'flex-start',
  background: '#FFF0EC',
  color: '#C03626',
  borderRadius: '999px',
  padding: '0.4rem 0.9rem',
  fontSize: '0.85rem',
  fontWeight: 600,
  display: 'inline-flex',
  alignItems: 'center',
};

const exampleTitle = {
  margin: 0,
  fontSize: '1.4rem',
  color: '#2B1610',
};

const exampleText = {
  margin: 0,
  color: '#5C3A32',
  lineHeight: 1.6,
};

const highlightSection = {
  maxWidth: 1100,
  margin: '0 auto 3.5rem',
};

const highlightCard = {
  background: 'transparent',
  borderRadius: 0,
  padding: 0,
  border: 'none',
  boxShadow: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: '1.2rem',
};

const envGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
  gap: '1.5rem',
  width: '100%',
  justifyItems: 'center',
};

const envRow = {
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
  padding: '0.5rem 0',
};

const envIcon = {
  width: 48,
  height: 48,
  borderRadius: '14px',
  background: '#FFF0EC',
  color: '#C03626',
  display: 'grid',
  placeItems: 'center',
  fontSize: '1.6rem',
};

const envText = {
  margin: 0,
  color: '#4A2E25',
  fontWeight: 600,
  lineHeight: 1.4,
};

const stepsGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: '1.5rem',
};

const stepCard = {
  background: 'transparent',
  borderRadius: 0,
  padding: 0,
  border: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
};

const stepNumber = {
  color: '#C03626',
  fontWeight: 700,
  letterSpacing: '0.08em',
  fontSize: '0.9rem',
};

const ctaSection = {
  maxWidth: 900,
  margin: '0 auto',
};

const ctaCard = {
  background: '#2A0D05',
  color: '#fff',
  borderRadius: '20px',
  padding: '2.4rem',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
};

const ctaTitle = {
  margin: 0,
  fontSize: '2rem',
};

const ctaText = {
  margin: 0,
  fontSize: '1.05rem',
  lineHeight: 1.7,
  color: '#FFEAE4',
};

export default Losninger;
